import { apiClient } from '@/shared/api/client';

export interface MaintenanceCheck {
  id: string;
  pondId: string;
  farmId: string;
  pond?: {
    id: string;
    name: string;
  };
  checkedAt: string;
  waterLevel: number;
  waterFlowCheck: boolean;
  paddleWheelFunctioning: boolean;
  stressedFishAtInlets: boolean;
  procedureFollowed?: string;
  algalBloom: boolean;
  presenceOfBirds: boolean;
  dischargePh?: number;
  dischargeDo?: number;
  dischargeFlowRate?: number;
  notes?: string;
  photosPaths?: string[];
  imageIds?: string[];
  hasPendingImages: boolean;
  latitude?: number | null;
  longitude?: number | null;
  createdBy: string;
  performedByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MaintenanceSummary {
  totalChecks: number;
  equipmentIssues: number;
  environmentalAlerts: number;
  averageWaterLevel: number;
  lastCheckDate?: string;
  paddleWheelIssues: number;
  waterFlowIssues: number;
  algalBloomCount: number;
  birdPresenceCount: number;
  stressedFishCount: number;
}

export interface MaintenanceStatistics {
  totalChecks: number;
  checksThisWeek: number;
  checksThisMonth: number;
  equipmentIssueCount: number;
  avgWaterLevel: number;
  avgDischargePh?: number;
  avgDischargeDo?: number;
  environmentalIssuesByType: {
    algalBloom: number;
    presenceOfBirds: number;
    stressedFish: number;
  };
}

export interface TrendDataPoint {
  date: string;
  value: number;
  pondId?: string;
  pondName?: string;
}

export interface MaintenanceAlert {
  id: string;
  checkId: string;
  pondId: string;
  pondName: string;
  alertType: 'equipment' | 'water_level' | 'environmental' | 'discharge';
  message: string;
  severity: 'info' | 'warning' | 'critical';
  timestamp: string;
  details?: Record<string, unknown>;
}

export interface MaintenanceCreateRequest {
  checkedAt: string
  waterLevel: number
  waterFlowCheck: boolean
  paddleWheelFunctioning: boolean
  stressedFishAtInlets: boolean
  algalBloom: boolean
  presenceOfBirds: boolean
  procedureFollowed?: string
  dischargePh?: number
  dischargeDo?: number
  dischargeFlowRate?: number
  notes?: string
  imageIds?: string[]
}

export type MaintenanceUpdateRequest = Partial<MaintenanceCreateRequest>

export const maintenanceApi = {
  // Create a new maintenance check
  create: async (
    farmId: string,
    pondId: string,
    data: MaintenanceCreateRequest
  ): Promise<MaintenanceCheck> => {
    const response = await apiClient.post<{ data: MaintenanceCheck }>(
      `/farms/${farmId}/ponds/${pondId}/maintenance`,
      data
    )
    return response.data.data
  },

  // Update an existing maintenance check
  update: async (
    farmId: string,
    pondId: string,
    checkId: string,
    data: MaintenanceUpdateRequest
  ): Promise<MaintenanceCheck> => {
    const response = await apiClient.put<{ data: MaintenanceCheck }>(
      `/farms/${farmId}/ponds/${pondId}/maintenance/${checkId}`,
      data
    )
    return response.data.data
  },

  // List maintenance checks for a farm
  listChecks: async (
    farmId: string,
    params?: {
      cursor?: string;
      limit?: number;
      pondId?: string;
      startDate?: string;
      endDate?: string;
      filter?: 'equipment_issues' | 'algal_bloom' | 'birds' | 'stressed_fish';
    }
  ) => {
    const response = await apiClient.get<{
      data: {
        checks: MaintenanceCheck[];
        pagination: { nextCursor?: string; hasNext: boolean };
      };
    }>(`/farms/${farmId}/maintenance`, { params });
    return { data: response.data.data.checks, pagination: response.data.data.pagination };
  },

  // Get maintenance checks for a specific pond
  getPondChecks: async (
    farmId: string,
    pondId: string,
    params?: { cursor?: string; limit?: number }
  ) => {
    const response = await apiClient.get<{
      data: MaintenanceCheck[];
      pagination: { nextCursor?: string; hasNext: boolean };
    }>(`/farms/${farmId}/ponds/${pondId}/maintenance`, { params });
    return response.data;
  },

  // Get a specific check
  getCheck: async (farmId: string, pondId: string, checkId: string) => {
    const response = await apiClient.get<{ data: MaintenanceCheck }>(
      `/farms/${farmId}/ponds/${pondId}/maintenance/${checkId}`
    );
    return response.data.data;
  },

  // Get maintenance summary for a farm
  getSummary: async (
    farmId: string,
    params?: { startDate?: string; endDate?: string }
  ) => {
    const response = await apiClient.get<MaintenanceSummary>(
      `/farms/${farmId}/maintenance/summary`,
      { params }
    );
    return response.data;
  },

  // Get trend data for a specific parameter
  getTrends: async (
    farmId: string,
    params: {
      parameter: 'water_level' | 'discharge_ph' | 'discharge_do' | 'discharge_flow_rate';
      days: number;
      pondId?: string;
    }
  ) => {
    const response = await apiClient.get<{ data: TrendDataPoint[] }>(
      `/farms/${farmId}/maintenance/trends`,
      { params }
    );
    return response.data;
  },

  // Get maintenance alerts
  getAlerts: async (farmId: string, params?: { severity?: string; type?: string }) => {
    const response = await apiClient.get<{
      data: MaintenanceAlert[];
    }>(`/farms/${farmId}/maintenance/alerts`, { params });
    return response.data.data || [];
  },

  // Get maintenance statistics for analytics
  getStatistics: async (farmId: string, params?: { pondIds?: string[]; days?: number }) => {
    const response = await apiClient.get<{
      data: MaintenanceStatistics;
    }>(`/farms/${farmId}/maintenance/statistics`, { params });
    return response.data.data;
  },

  // Export checks as CSV
  exportChecks: async (
    farmId: string,
    params?: { pondId?: string; startDate?: string; endDate?: string }
  ) => {
    const response = await apiClient.get(`/farms/${farmId}/maintenance/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};
